/**
 * HomeSkincareTipsSection.jsx — Feb 2026
 * Horizontal-scroll strip pulling Karine's latest routine + product tips
 * (12 SC + Eminence tips added in iter 374). Fetches lightweight page list
 * from /api/skincare-101/pages, filters to the 12 new SC/Eminence routine
 * slugs, and renders each as a compact card linking to /skincare-101/{slug}.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { BookOpen, ArrowRight, Clock } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Curated order matches Karine's routine story: cleanse → tone → protect → treat.
// Iter 375 · Feb 20 2026: the 4 new Top-25 CA gap SKUs (Discoloration Defense,
// LHA Serum, Glycolic 10, Metacell B3) lead the strip so first-time
// visitors discover them immediately.
const FEATURED_SLUGS = [
  // New Top-25 gap articles (lead the strip)
  "discoloration-defense-the-serum-i-reach-for-between-chemical-peels",
  "lha-serum-for-adult-acne-that-still-wants-to-look-young",
  "glycolic-10-renew-overnight-karine-s-nightly-glow-reset",
  "metacell-b3-the-daily-moisturizer-that-rebuilds-early-signs-of-aging",
  // Established Karine rituals
  "karine-s-morning-3-minute-antioxidant-ritual",
  "karine-s-sunday-reset-the-15-minute-weekly-ritual",
  "p-tiox-the-60-second-peptide-push-karine-does-every-night",
  "phloretin-cf-vs-c-e-ferulic-which-one-is-for-you",
  "strawberry-rhubarb-hyaluronic-serum-the-drink-of-water-your-skin-actually-feels",
  "aox-eye-gel-the-only-eye-product-i-use-after-filler",
  "bright-skin-licorice-root-booster-serum-the-post-acne-mark-eraser",
  "bamboo-firming-fluid-the-one-moisturizer-that-works-for-every-skin-type",
  "neroli-age-corrective-eye-serum-the-first-product-i-apply-after-botox",
  "lilikoi-mineral-defense-spf-33-the-post-laser-spf-i-send-home-with-every-client",
];

const CATEGORY_TINT = {
  "SkinCeuticals & At-Home": {
    ribbon: "bg-slate-900 text-white",
    label: "SkinCeuticals",
  },
  "Eminence & At-Home": {
    ribbon: "bg-emerald-800 text-white",
    label: "Eminence Organics",
  },
};

// Slugs that get a bright "NEW" corner badge — the 4 Feb 2026 Top-25
// CA gap articles Karine wants front-and-centre for first-time visitors.
const NEW_SLUGS = new Set([
  "discoloration-defense-the-serum-i-reach-for-between-chemical-peels",
  "lha-serum-for-adult-acne-that-still-wants-to-look-young",
  "glycolic-10-renew-overnight-karine-s-nightly-glow-reset",
  "metacell-b3-the-daily-moisturizer-that-rebuilds-early-signs-of-aging",
]);

const T = {
  en: {
    eyebrow: "Karine's Skincare 101",
    heading: "Latest skincare tips",
    lede: "Real-world routines from Karine — with the exact products, timing, and application steps she uses on her own face.",
    all: "See all tips",
    read: "Read",
  },
  fr: {
    eyebrow: "Skincare 101 de Karine",
    heading: "Derniers conseils",
    lede: "Des routines concrètes de Karine — avec les produits, moments et gestes exacts qu'elle utilise elle-même.",
    all: "Voir tous les conseils",
    read: "Lire",
  },
};

export default function HomeSkincareTipsSection() {
  const { language } = useLanguage();
  const t = T[language] || T.en;
  const [tips, setTips] = useState([]);

  useEffect(() => {
    axios.get(`${API}/skincare-101/pages`).then((r) => {
      const pages = r.data?.pages || [];
      const bySlug = new Map(pages.map((p) => [p.slug, p]));
      const ordered = FEATURED_SLUGS.map((s) => bySlug.get(s)).filter(Boolean);
      setTips(ordered);
    }).catch(() => setTips([]));
  }, []);

  if (tips.length === 0) return null;

  return (
    <section className="py-14 px-4 sm:px-6 bg-gradient-to-b from-[#fbfaf6] to-emerald-50/40"
             data-testid="home-skincare-tips-section"
             aria-labelledby="home-skincare-tips-heading">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-6 gap-3">
          <div>
            <p className="uppercase tracking-[0.28em] text-[10px] text-emerald-700 font-black mb-2 flex items-center gap-1.5">
              <BookOpen className="w-3.5 h-3.5" /> {t.eyebrow}
            </p>
            <h2 id="home-skincare-tips-heading" className="font-serif text-3xl sm:text-4xl text-slate-900 leading-tight">
              {t.heading}
            </h2>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">{t.lede}</p>
          </div>
          <Link to="/skincare-101"
                className="text-xs uppercase tracking-widest text-emerald-800 hover:text-emerald-950 shrink-0 pb-1 flex items-center gap-1"
                data-testid="home-tips-see-all">
            {t.all} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <div className="flex gap-4 overflow-x-auto no-scrollbar snap-x snap-mandatory pb-2 -mx-1 px-1">
          {tips.map((tip) => {
            const tint = CATEGORY_TINT[tip.category] || {
              ribbon: "bg-slate-700 text-white", label: tip.category,
            };
            return (
              <Link key={tip.slug}
                    to={`/skincare-101/${tip.slug}`}
                    className="relative snap-start shrink-0 w-[calc(85%)] sm:w-[300px] md:w-[320px] bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl hover:border-emerald-200 transition-all p-5 flex flex-col group"
                    data-testid={`home-tip-card-${tip.slug}`}>
                {NEW_SLUGS.has(tip.slug) && (
                  <span className="absolute -top-2 -right-2 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-rose-600 text-white text-[10px] font-black uppercase tracking-widest shadow-md"
                        data-testid={`home-tip-new-badge-${tip.slug}`}>
                    <span className="w-1 h-1 rounded-full bg-white animate-pulse" /> New
                  </span>
                )}
                <div className="flex items-center gap-2 mb-3">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold ${tint.ribbon}`}>
                    {tint.label}
                  </span>
                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                    <Clock className="w-2.5 h-2.5" /> 1 min
                  </span>
                </div>
                <h3 className="font-serif text-lg text-slate-900 leading-snug mb-2 line-clamp-3">
                  {tip.title}
                </h3>
                {tip.subtitle && (
                  <p className="text-xs text-slate-600 line-clamp-3 mb-3 flex-1">{tip.subtitle}</p>
                )}
                <span className="mt-auto inline-flex items-center gap-1 text-xs uppercase tracking-wide font-bold text-emerald-800 group-hover:text-emerald-950">
                  {t.read} <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
}
